import {useEffect, useState, useSyncExternalStore, type CSSProperties} from 'react';
import {useIntl} from 'react-intl';
import type {RingTargetStatus, RingTargetState} from '@phonehq/rtc-core';
import {useCallMember} from '../../groupCalls/components/callContext';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import type {RingMembersOutcome} from '../../groupCalls/services/groupCallOrchestrator';
import {MemberAvatar} from '../components/MemberAvatar';
import {groupSurfaceMessages as m} from './groupMessages';

const STATUS_TEXT: Readonly<Record<RingTargetStatus, keyof typeof m | undefined>> = {
    RINGING: 'inviteRinging',
    SILENT: 'inviteSilent',
    MISSED: 'inviteNoAnswer',
    CANCELLED: 'inviteNoAnswer',
    DECLINED: 'inviteDeclined',
    BUSY: 'inviteBusy',
    ANSWERED: undefined,
};

const RING_ERRORS: Partial<Record<RingMembersOutcome, keyof typeof m>> = {
    full: 'ringFull',
    not_joined: 'ringNotJoined',
    failed: 'ringFailed',
};

const canRingAgain = (status: RingTargetStatus): boolean => status === 'MISSED' || status === 'CANCELLED' || status === 'DECLINED' || status === 'BUSY';

const OPTIMISTIC: Partial<Record<RingMembersOutcome, RingTargetStatus>> = {
    ringing: 'RINGING',
    notified: 'SILENT',
};

const useRingCooldown = (matrixUserId: string): number => {
    const until = useSyncExternalStore(
        groupCallService.ringCooldowns.subscribe,
        () => groupCallService.ringCooldowns.getSnapshot().get(matrixUserId) ?? 0,
    );
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        setNow(Date.now());
        if (until <= Date.now()) {
            return;
        }
        const timer = window.setInterval(() => {
            setNow(Date.now());
            if (Date.now() >= until) {
                window.clearInterval(timer);
            }
        }, 1_000);
        return () => window.clearInterval(timer);
    }, [until]);
    return Math.max(0, Math.ceil((until - now) / 1_000));
};

const useRingAgain = (target: RingTargetState) => {
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<keyof typeof m>();
    const [optimistic, setOptimistic] = useState<{status: RingTargetStatus; baseline: string}>();
    const status = optimistic && optimistic.baseline === target.updatedAt ? optimistic.status : target.status;
    const ringAgain = async () => {
        setPending(true);
        setError(undefined);
        const baseline = target.updatedAt;
        const outcome = await groupCallService.ringMembers([target.matrixUserId]);
        setPending(false);
        setError(RING_ERRORS[outcome]);
        const next = OPTIMISTIC[outcome];
        setOptimistic(next ? {status: next, baseline} : undefined);
    };
    const cooldown = useRingCooldown(target.matrixUserId);
    return {status, statusKey: error ?? STATUS_TEXT[status], canRing: canRingAgain(status) && !pending, cooldown, ringAgain};
};

const RingAgainButton = ({name, cooldown, onClick}: {name: string; cooldown: number; onClick: () => void}) => {
    const intl = useIntl();
    const waiting = cooldown > 0;
    return (
        <button
            type="button"
            data-testid="group-call-ring-again"
            data-cooldown={waiting ? cooldown : undefined}
            aria-label={intl.formatMessage(waiting ? m.ringAgainInAria : m.ringAgainAria, {name, seconds: cooldown})}
            disabled={waiting}
            onClick={onClick}
            className={`inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-lg bg-[var(--phq-call-surface-muted)] px-3 text-xs font-semibold tabular-nums ${waiting ? 'cursor-default opacity-60' : 'phq-call-press'}`}
        >
            {waiting ? intl.formatMessage(m.ringAgainIn, {seconds: cooldown}) : intl.formatMessage(m.ringAgain)}
        </button>
    );
};

export const InviteeRow = ({target}: {target: RingTargetState}) => {
    const intl = useIntl();
    const profile = useCallMember(target.matrixUserId);
    const {status, statusKey, canRing, cooldown, ringAgain} = useRingAgain(target);
    return (
        <li className="flex h-12 items-center gap-3 px-4" data-testid="group-call-invitee-row" data-status={status}>
            <MemberAvatar userId={target.matrixUserId} name={profile.name} mxcAvatarUrl={profile.mxcAvatarUrl} size={32}/>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{profile.name}</p>
                {statusKey && <p className="truncate text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m[statusKey])}</p>}
            </div>
            {canRing ? (
                <RingAgainButton name={profile.name} cooldown={cooldown} onClick={() => void ringAgain()}/>
            ) : (
                <span className="shrink-0 text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.peopleStatusInvited)}</span>
            )}
        </li>
    );
};

export const InviteeTile = ({target, className = '', style}: {target: RingTargetState; className?: string; style?: CSSProperties}) => {
    const intl = useIntl();
    const profile = useCallMember(target.matrixUserId);
    const {status, statusKey, canRing, cooldown, ringAgain} = useRingAgain(target);
    return (
        <div
            data-testid="group-call-invitee-tile"
            data-status={status}
            style={style}
            className={`relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl bg-[var(--phq-call-surface)] p-4 text-center ${className}`}
        >
            <span className={status === 'RINGING' ? 'phq-call-ring rounded-full' : undefined}>
                <MemberAvatar userId={target.matrixUserId} name={profile.name} mxcAvatarUrl={profile.mxcAvatarUrl} size={64}/>
            </span>
            {statusKey && <p className="text-[13px] font-medium text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m[statusKey])}</p>}
            {canRing && <RingAgainButton name={profile.name} cooldown={cooldown} onClick={() => void ringAgain()}/>}
            <p className="absolute bottom-3 left-3 max-w-[calc(100%-24px)] truncate rounded-md bg-black/40 px-2 py-0.5 text-xs font-medium">
                {intl.formatMessage(m.inviteeTileName, {name: profile.name})}
            </p>
        </div>
    );
};

export const InviteesSection = ({invitees}: {invitees: readonly RingTargetState[]}) => {
    const intl = useIntl();
    if (invitees.length === 0) {
        return null;
    }
    return (
        <>
            <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[.04em] text-[var(--phq-call-ink-muted)]">
                {intl.formatMessage(m.peopleInvited, {count: invitees.length})}
            </p>
            <ul className="flex flex-col" data-testid="group-call-invitees">
                {invitees.map(target => <InviteeRow key={target.matrixUserId} target={target}/>)}
            </ul>
        </>
    );
};
